import { OptionFieldValidator } from "./option-field-validator";
import { ValidatorFn, ValidatorError } from "./validation";
import { html } from "./dom-utils";

export class OptionFieldManager {
  private eventListeners: Array<{
    element: Element;
    event: string;
    handler: EventListener;
  }> = [];

  constructor(private fieldGroup: HTMLElement) {}

  getHiddenInput(): HTMLInputElement | null {
    return this.fieldGroup.querySelector("[data-option-field-hidden]");
  }

  getVisibleInput(): HTMLInputElement | null {
    return this.fieldGroup.querySelector("[data-option-field-visible]");
  }

  getValidationSpan(): HTMLElement | null {
    const hiddenInput = this.getHiddenInput();
    if (!hiddenInput) return null;

    // Look for validation span with matching data-validation-for attribute
    return document.querySelector(`[data-validation-for="${hiddenInput.id}"]`);
  }

  private addEventListener(
    element: Element,
    event: string,
    handler: EventListener,
  ): void {
    element.addEventListener(event, handler);
    this.eventListeners.push({ element, event, handler });
  }

  cleanup(): void {
    for (const { element, event, handler } of this.eventListeners) {
      element.removeEventListener(event, handler);
    }
    this.eventListeners = [];
  }

  private displayValidationErrors(
    errors: ValidatorError[],
    userHasInteracted: boolean,
  ): void {
    const validationSpan = this.getValidationSpan();
    if (!validationSpan) return;

    // Only show errors if user has interacted with the field
    if (!userHasInteracted) {
      validationSpan.innerHTML = "&nbsp;";
      return;
    }

    validationSpan.innerHTML = errors.length === 0 ? "&nbsp;" : "";

    for (const error of errors) {
      const errorEl = html`<div>${error.message}</div>`;
      if (errorEl instanceof Array) throw Error("Should only get one element");
      validationSpan.appendChild(errorEl);
    }
  }

  private updateRequiredAttributes(
    hiddenInput: HTMLInputElement,
    visibleInput: HTMLInputElement,
  ): void {
    const isMultiselect = hiddenInput.hasAttribute("data-multiselect-required");
    const isTypeahead = visibleInput.hasAttribute("required") && !isMultiselect;

    if (isMultiselect) {
      const hasSelections =
        hiddenInput.value && hiddenInput.value.trim().length > 0;
      if (hasSelections) {
        visibleInput.removeAttribute("required");
      } else {
        visibleInput.setAttribute("required", "");
      }
    } else if (isTypeahead) {
      visibleInput.setAttribute("required", "");
    }
  }

  private setupEventListeners(
    hiddenInput: HTMLInputElement,
    visibleInput: HTMLInputElement,
    userHasInteracted: { value: boolean },
    updateValidationState: () => void,
  ): void {
    const inputHandler = () => {
      userHasInteracted.value = true;
      updateValidationState();
    };
    this.addEventListener(visibleInput, "input", inputHandler);

    const blurHandler = () => {
      userHasInteracted.value = true;
      updateValidationState();
    };
    this.addEventListener(visibleInput, "blur", blurHandler);

    // CRITICAL: Also listen for changes to hidden input
    // This is essential for async typeaheads where dropdown selections
    // update the hidden input without triggering visible input events
    const hiddenInputHandler = () => {
      userHasInteracted.value = true;
      updateValidationState();
    };
    this.addEventListener(hiddenInput, "change", hiddenInputHandler);
    this.addEventListener(hiddenInput, "input", hiddenInputHandler);
  }

  private setupFormValidation(
    visibleInput: HTMLInputElement,
    userHasInteracted: { value: boolean },
    updateValidationState: () => void,
  ): void {
    const form = visibleInput.closest("form");
    if (form) {
      const formSubmitHandler = (e: Event) => {
        userHasInteracted.value = true;
        updateValidationState();
      };
      this.addEventListener(form, "submit", formSubmitHandler);
    }
  }

  setupValidation(
    validator: OptionFieldValidator,
    additionalValidators: ValidatorFn<string>[] = [],
    fieldName: string = "Field",
  ): void {
    const hiddenInput = this.getHiddenInput();
    const visibleInput = this.getVisibleInput();

    if (!hiddenInput || !visibleInput) {
      const errorMsg = `Option field validation setup failed - missing required inputs. Expected elements with data-option-field-hidden and data-option-field-visible attributes in field group.`;
      console.error(errorMsg, {
        fieldGroup: this.fieldGroup,
        hiddenInput: hiddenInput,
        visibleInput: visibleInput,
        fieldName: fieldName,
      });

      if (process.env.NODE_ENV === "development") {
        throw new Error(errorMsg);
      }

      return;
    }

    const userHasInteracted = { value: false };

    const updateValidationState = () => {
      const visibleValue = visibleInput.value;

      let error: ValidatorError | null = null;

      error = validator.validateVisibleInput(visibleValue);

      if (!error) {
        for (const additionalValidator of additionalValidators) {
          error = additionalValidator(visibleValue);
          if (error) break;
        }
      }

      const isRequired =
        hiddenInput.hasAttribute("data-multiselect-required") ||
        visibleInput.hasAttribute("required");
      if (!error && isRequired) {
        error = validator.validateRequired(visibleValue, fieldName);
      }

      if (error) {
        visibleInput.setCustomValidity(error.message);
      } else {
        visibleInput.setCustomValidity("");
      }

      const errors = error ? [error] : [];
      this.displayValidationErrors(errors, userHasInteracted.value);

      this.updateRequiredAttributes(hiddenInput, visibleInput);
    };

    updateValidationState();
    this.setupEventListeners(
      hiddenInput,
      visibleInput,
      userHasInteracted,
      updateValidationState,
    );
    this.setupFormValidation(
      visibleInput,
      userHasInteracted,
      updateValidationState,
    );
  }
}

export class TypeaheadFieldManager extends OptionFieldManager {
  constructor(
    fieldGroup: HTMLElement,
    private hiddenInputElement: HTMLInputElement,
    private visibleInputElement: HTMLInputElement,
  ) {
    super(fieldGroup);
  }

  getHiddenInput(): HTMLInputElement | null {
    return this.hiddenInputElement;
  }

  getVisibleInput(): HTMLInputElement | null {
    return this.visibleInputElement;
  }
}
